/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type React from 'react';

/**
 * The settings block pattern's tokens, transcribed from the approved
 * prototype rather than approximated.
 *
 * The card carries **no padding**: the header and the mid each bring their
 * own, separated by a hairline. Putting the padding on the card instead —
 * which is how this started — makes every mid fight it with a margin, and
 * leaves the header without the rule that divides it from the content.
 *
 * One card, one header, one of six mids. A section that needs a seventh mid
 * needs it added here, not a private layout of its own.
 */

/** The block card. No padding, and clipped so the header rule meets the edge. */
export const blockCardStyle: React.CSSProperties = {
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--surface-raised)',
  overflow: 'hidden',
};

/** Block header: title and caption on the left, at most one action right. */
export const blockHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 'var(--space-4)',
  flexWrap: 'wrap',
  padding: 'var(--space-4) var(--space-5)',
  borderBottom: '1px solid var(--border-ultra-subtle)',
};

/** The title/caption column of a block header; yields width to the action. */
export const blockHeaderTextStyle: React.CSSProperties = {
  flex: '1 1 20rem',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

export const blockTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  fontWeight: 700,
  color: 'var(--text-primary)',
};

/**
 * The block caption — where the explanation that used to float above the
 * cards now lives. Capped at 68ch: a full-width line of tertiary text reads
 * as debris rather than as prose.
 */
export const blockCaptionStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-small)',
  color: 'var(--text-tertiary-soft)',
  lineHeight: 1.5,
  maxWidth: '68ch',
};

/** The header's single action: a 32px outline button, never a filled one. */
export const blockActionStyle: React.CSSProperties = {
  height: 32,
  padding: '0 var(--space-3-5)',
  border: '1px solid var(--border-medium)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--surface-primary)',
  fontSize: 'var(--text-body-sm)',
  fontWeight: 600,
  color: 'var(--text-primary)',
  cursor: 'pointer',
  flex: '0 0 auto',
  whiteSpace: 'nowrap',
  fontFamily: 'var(--font-family)',
};

/** The padding every mid carries, since the card carries none. */
export const blockMidPadding = 'var(--space-4) var(--space-5)';

/** A row inside a list mid (`people`, `toggles`), divided by a hairline. */
export const blockRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-3) var(--space-5)',
  borderBottom: '1px solid var(--border-ultra-subtle)',
};

/** The `fields` mid's grid. Reflows on its own; no breakpoint drives it. */
export const blockFieldsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 'var(--space-4) var(--space-5)',
  padding: blockMidPadding,
};

export const fieldLabelStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  color: 'var(--text-tertiary-soft)',
};

/**
 * Builds the style for a field's value.
 *
 * An absent value is always *written* — "não informado" — never elided into a
 * neutral dash, because a dash disappears in the middle of the grid. What
 * varies is the tone, and the tone is a promise:
 *
 * - `--accent-cobre` means the pending band is counting this field. Copper
 *   asks to be acted on, and the chip is where the acting starts.
 * - The tertiary tone means the field is legitimately optional — an agency
 *   with no website is not an agency with a problem.
 *
 * Only the colour changes. The prototype does not bold an absent value, and
 * bolding it would make the gaps louder than the data.
 *
 * @param isMissing - Whether the field has no value.
 * @param isOptional - Whether absence is acceptable rather than pending.
 * @returns The CSS properties for the value line.
 */
export const fieldValueStyle = (isMissing: boolean, isOptional = false): React.CSSProperties => ({
  margin: 0,
  fontSize: 'var(--text-body-sm)',
  color: missingValueTone(isMissing, isOptional),
  lineHeight: 1.45,
});

/**
 * Picks the tone for a field value.
 *
 * @param isMissing - Whether the field has no value.
 * @param isOptional - Whether absence is acceptable rather than pending.
 * @returns The colour token reference.
 */
function missingValueTone(isMissing: boolean, isOptional: boolean): string {
  if (!isMissing) return 'var(--text-primary)';
  return isOptional ? 'var(--text-tertiary-soft)' : 'var(--accent-cobre)';
}

/** The caveat line under a mid, attached to what it qualifies. */
export const blockNoteStyle: React.CSSProperties = {
  margin: 0,
  padding: '0 var(--space-5) var(--space-4)',
  fontSize: 'var(--text-meta)',
  color: 'var(--text-tertiary-soft)',
  lineHeight: 1.5,
  maxWidth: '68ch',
};
